import { useMemo } from 'react'
import type { UIMessage } from 'ai'
import { useTranslation } from 'react-i18next'
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning'

type ReasoningPart = Extract<UIMessage['parts'][number], { type: 'reasoning' }>

export type LlmChatAssistantReasoningPartProps = {
  part: ReasoningPart
  isActiveAssistantTurn: boolean
  isAwaitingAssistantFirstChunk: boolean
  isAssistantResponseStreaming: boolean
  hasAssistantTextParts: boolean
}

export function LlmChatAssistantReasoningPart({
  part,
  isActiveAssistantTurn,
  isAwaitingAssistantFirstChunk,
  isAssistantResponseStreaming,
  hasAssistantTextParts,
}: LlmChatAssistantReasoningPartProps) {
  const { t } = useTranslation('llmChat')
  const isPartStreaming = part.state === 'streaming'

  const shouldKeepReasoningOpenWhileAwaitingNetwork =
    isActiveAssistantTurn && isAwaitingAssistantFirstChunk
  const shouldKeepReasoningOpenWhileStreamingBeforeText =
    isActiveAssistantTurn &&
    isAssistantResponseStreaming &&
    !hasAssistantTextParts

  const isStreaming =
    isPartStreaming ||
    shouldKeepReasoningOpenWhileAwaitingNetwork ||
    shouldKeepReasoningOpenWhileStreamingBeforeText

  const getThinkingMessage = useMemo(
    () => (streaming: boolean, duration?: number) => {
      if (streaming) {
        return <span>{t('reasoningThinking')}</span>
      }
      if (duration === undefined) {
        return <span>{t('reasoningThoughtBrief')}</span>
      }
      return <span>{t('reasoningThoughtSeconds', { count: duration })}</span>
    },
    [t]
  )

  return (
    <Reasoning
      isStreaming={isStreaming}
      className='rounded-md border border-border bg-muted/25 px-3 py-2'
    >
      <ReasoningTrigger
        getThinkingMessage={getThinkingMessage}
        className='text-xs font-medium'
      />
      <ReasoningContent className='mt-2 text-[12px] leading-relaxed'>
        {part.text}
      </ReasoningContent>
    </Reasoning>
  )
}
